<script setup>
import { hideElement, isMobileView, qs } from '@utils/toolbox';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import CalendarMonth from './CalendarMonth.vue';
import { useCalendar } from '../composables/useCalendar';

const props = defineProps({
	autoSearch: {
		type: Boolean,
		default: false
	}
});

const {
	init,
	calendarMonths,
	getAdjacentDate,
	weekdays,
	setupVisibilityObserver,
	leftDisabled,
	rightDisabled,
	scrollCalendars,
	updateSliderState,
	maxDigitsClass,
	queueNearbyDates,
	firstCheapestDate
	// debugState
} = useCalendar();

init({
	todayISO: js_params.isoDates.today,
	weekdays: js_params.weekdays,
	dateRange: js_params.dateRange
});

// ── Refs ──────────────────────────────────────────────────────────────────────
const innerRef = ref(null);
const gridRef = ref(null);
const scrollTimer = null;

// ── Scroll to active day ──────────────────────────────────────────────────────
function scrollToActive () {
	if (!innerRef.value || !gridRef.value) {
		return;
	}

	const activeDay = gridRef.value.querySelector('.Calendar__day--active');
	if (!activeDay) {
		return;
	}
	const calendar = activeDay.closest('.Calendar');
	if (!calendar) {
		return;
	}

	const dateISO = activeDay.dataset?.date;
	const isFirstWeek = dateISO ? new Date(dateISO).getDate() <= 7 : false;

	if (isMobileView()) {
		const prevMonthPeek = isFirstWeek ? 91 : 0;
		innerRef.value.scrollTop = calendar.offsetTop - 55 - prevMonthPeek;
	} else {
		innerRef.value.scrollLeft = calendar.offsetLeft;
	}
}

function scrollToCheapest () {
	const cheapest = firstCheapestDate.value;
	if (!cheapest) {
		return;
	}
	const target = gridRef.value.querySelector(`[data-date="${cheapest}"]`);
	if (target) {
		target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
	}
}

// ── Slider Scroll ─────────────────────────────────────────────────────────────
function onScroll () {
	if (innerRef.value) {
		updateSliderState(innerRef.value);
	}
}

function handleSlide (direction) {
	scrollCalendars(direction, innerRef.value);
}

// ── Keyboard ──────────────────────────────────────────────────────────────────
function onKeyup (event) {
	if (event.key === 'Escape') {
		return;
	}

	const currentISO = document.activeElement?.dataset?.date;

	if (event.key === 'Enter' || event.key === 'Space') {
		if (currentISO) {
			document.activeElement.click();
			const nextISO = getAdjacentDate(currentISO, event.key);
			if (nextISO) {
				nextTick(() => {
					gridRef.value?.querySelector(`[data-date="${nextISO}"]`)?.focus();
				});
			}
		}
		return;
	}

	if (currentISO) {
		const targetISO = getAdjacentDate(currentISO, event.key);
		if (targetISO) {
			nextTick(() => {
				gridRef.value?.querySelector(`[data-date="${targetISO}"]`)?.focus();
			});
		}
	}
}


onMounted(() => {
	// Bind keyboard listener for accessibility
	document.addEventListener('keyup', onKeyup);

	// Bind an intersection observer to each day
	setupVisibilityObserver();

	// Wait for next animation frame to ensure calendar is rendered before scrolling to active day
	// This is because offsetLeft/offsetTop might be 0 if the calendar is not fully rendered yet
	requestAnimationFrame(scrollToActive);

	if (props.autoSearch) {
		queueNearbyDates();
	}

	qs('[data-action="center-calendar"]')?.addEventListener('click', scrollToActive);
	qs('.priceCalendar__key--cheap')?.addEventListener('click', scrollToCheapest);
});

// Forgive me father, for I have sinned.
onBeforeMount(() => {
	qs('.priceCalendar__inner').style.maxHeight = '100%';
	hideElement('.priceCalendar__maximize');
});

onUnmounted(() => {
	document.removeEventListener('keyup', onKeyup);
	if (scrollTimer) {
		clearTimeout(scrollTimer);
	}
});
</script>

<template>
  <div
    :class="[
      'abCalendarWrapper',
      'abCalendarWrapper--visible',
      maxDigitsClass
    ]"
  >
    <!-- <CalendarHeader title="Choose departure date" /> -->

    <div class="aCalendarRenderer__main">
      <CalendarSliders
        :left-disabled="leftDisabled"
        :right-disabled="rightDisabled"
        @slide="handleSlide"
      />

      <div role="dialog" class="calendarsContainer">
        <div
          ref="gridRef"
          role="grid"
          class="Calendars"
        >
          <div
            ref="innerRef"
            class="Calendars__inner"
            tabindex="-1"
            @scroll="onScroll"
          >
            <CalendarMonth
              v-for="month in calendarMonths"
              :key="month.key"
              v-bind="month"
            />
          </div>

          <div class="Calendars__dayspan">
            <span v-for="day in weekdays" :key="day">{{ day }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- <button
      class="abBtn abBtn--blueWhite"
      @click="debugState"
    >
      Set debug state
    </button> -->

    <!-- <CalendarFooter :label="applyLabel" @apply="apply" /> -->
  </div>
</template>


<style scoped lang="scss">
.abCalendarWrapper {
  max-width: initial;
  width: 100%;
}

.aCalendarRenderer__main {
  top: 0;
  padding-bottom: .5rem;
  width: 100%;
  overflow: hidden;
}

.abCalendarWrapper--visible {
  top: 0;
  position: relative;
}

.Calendars {
	padding: 0;
}

.abCalendarWrapper__header {
	display: none;
}

.abCalendarWrapper--visible {
	pointer-events: auto;
}
</style>
